import { useState } from "react";
import { useTheme } from "../../../context/ThemeContext";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPowerOff,
  faPlay,
  faPause,
  faMagnifyingGlass,
  faFileCode,
  faDownload,
  faImage,
  faCopy,
  faCheck,
  faTimes,
  faSync,
  faChevronDown,
  faKeyboard,
  faRotateRight,
} from "@fortawesome/free-solid-svg-icons";

import { useXmlMonitor } from "../hooks/useXmlMonitor";
import { escapeRegex } from "../helpers";
import { StatusDot } from "./StatusDot";
import { ProgressBar } from "./ProgressBar";
import { XmlSummary } from "./XmlSummary";
import type { Toast } from "../types";

// ─── Toast colors ───────────────────────────────────────────────────────────

const TOAST_STYLES: Record<Toast["type"], string> = {
  info: "bg-blue-600 text-white",
  success: "bg-emerald-600 text-white",
  warning: "bg-amber-500 text-dark-bg",
  error: "bg-red-600 text-white",
};

// ─── Style tokens ───────────────────────────────────────────────────────────

function useStyleTokens(isDarkMode: boolean) {
  const inpCls = isDarkMode
    ? "bg-dark-card border border-dark-border rounded text-slate-100 text-sm px-2.5 py-1.5 outline-none focus:border-primary-500"
    : "bg-white border border-gray-300 rounded text-gray-800 text-sm px-2.5 py-1.5 outline-none focus:border-primary-500";

  const btnPrimary =
    "bg-primary-500 hover:bg-primary-400 border border-primary-400 rounded text-sm px-3.5 py-1.5 text-dark-bg font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-colors";

  const btnGhost = isDarkMode
    ? "bg-transparent border border-dark-border rounded text-xs px-2.5 py-1 text-gray-400 cursor-pointer hover:bg-dark-surface"
    : "bg-transparent border border-gray-300 rounded text-xs px-2.5 py-1 text-gray-500 cursor-pointer hover:bg-gray-100";

  return { inpCls, btnPrimary, btnGhost };
}

// ─── Main Component ─────────────────────────────────────────────────────────

export default function XmlMonitor() {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation("xml");
  const { inpCls, btnPrimary, btnGhost } = useStyleTokens(isDarkMode);
  const m = useXmlMonitor();
  const [mobileView, setMobileView] = useState<"terminal" | "xml">("terminal");

  const terminalMatches = m.terminalSearch
    ? m.lines.filter((l) => l.toLowerCase().includes(m.terminalSearch.toLowerCase())).length
    : 0;

  return (
    <div className={`flex flex-col h-full overflow-hidden ${isDarkMode ? "bg-dark-bg" : "bg-gray-50"}`}>

      {/* ─── RENDER COLD-START BANNER ────────────────────────────────────── */}
      {m.serversLoading && (
        <div className="bg-amber-500 text-dark-bg px-4 py-2.5 text-sm font-medium flex items-center justify-center gap-3 shrink-0 animate-pulse">
          <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          {t("banner.connecting")}
        </div>
      )}
      {m.serversError && !m.serversLoading && (
        <div className="bg-red-600 text-white px-4 py-2.5 text-sm font-medium flex items-center justify-center gap-3 shrink-0">
          <FontAwesomeIcon icon={faTimes} />
          {t("banner.connectError")}
          <button
            onClick={m.retryServers}
            className="ml-2 px-3 py-1 bg-white text-red-600 rounded text-xs font-bold hover:bg-red-100 transition-colors cursor-pointer"
          >
            <FontAwesomeIcon icon={faSync} className="mr-1.5" />
            {t("banner.retry")}
          </button>
        </div>
      )}

      {/* ─── TOOLBAR ─────────────────────────────────────────────────────── */}
      <div className={`px-3 sm:px-4 py-2 min-h-12 flex flex-wrap items-center gap-2 shrink-0 border-b shadow-sm ${
        isDarkMode ? "bg-dark-bg border-dark-border" : "bg-white border-gray-200"
      }`}>

        {/* SSH group */}
        <div className="w-full sm:w-auto flex items-center gap-2 flex-wrap min-w-0">
          <select
            value={m.serverId}
            onChange={(e) => m.changeServer(e.target.value)}
            className={`${inpCls} min-w-0 flex-1 sm:flex-none sm:min-w-[200px]`}
          >
            {m.servers.length === 0
              ? <option value="">{t("ssh.loadingServers")}</option>
              : m.servers.map((s) => <option key={s.id} value={s.id}>{s.label} — {s.host}</option>)
            }
          </select>

          {m.sshStatus === "connected" ? (
            <div className="flex items-center gap-1.5">
              <button onClick={m.connectServer} disabled={!m.serverId} className={btnPrimary} title={t("ssh.reconnect")}>
                <FontAwesomeIcon icon={faSync} className="text-xs" />
              </button>
              <button
                onClick={m.disconnectServer}
                className="bg-red-500 hover:bg-red-600 border border-red-600 rounded text-sm px-3 py-1.5 text-white font-medium cursor-pointer flex items-center gap-1.5"
                title={t("ssh.disconnectTitle")}
              >
                <FontAwesomeIcon icon={faPowerOff} className="text-xs" />
                <span className="hidden sm:inline">{t("ssh.disconnect")}</span>
              </button>
            </div>
          ) : (
            <button onClick={m.connectServer} disabled={!m.serverId || !m.socketReady || m.sshStatus === "reconnecting"} className={btnPrimary}>
              {m.sshStatus === "connecting" ? t("ssh.connecting") : m.sshStatus === "reconnecting" ? t("ssh.reconnecting") : t("ssh.connect")}
            </button>
          )}

          {/* SSH status pill */}
          <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium ${
            m.sshStatus === "connected" ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" :
            m.sshStatus === "connecting" ? "bg-amber-500/15 text-amber-400 border border-amber-500/30" :
            m.sshStatus === "reconnecting" ? "bg-orange-500/15 text-orange-400 border border-orange-500/30" :
            m.sshStatus === "error" ? "bg-red-500/15 text-red-400 border border-red-500/30" :
            isDarkMode ? "bg-dark-card text-gray-400 border border-dark-border" : "bg-gray-100 text-gray-500 border border-gray-200"
          }`}>
            <StatusDot status={m.sshStatus} />
            <span className="max-w-[220px] truncate" title={m.sshMsg}>{m.sshMsg}</span>
          </div>
        </div>

        <div className={`hidden sm:block w-px h-6 ${isDarkMode ? "bg-dark-border" : "bg-gray-200"}`} />

        {/* Session selector */}
        <div className="relative w-full sm:w-auto min-w-0">
          {m.customSession ? (
            <div className="flex items-center gap-1">
              <input
                placeholder={t("session.exactName")}
                value={m.session}
                onChange={(e) => m.setSession(e.target.value)}
                className={`${inpCls} flex-1 sm:flex-none sm:w-56 min-w-0`}
                autoFocus
              />
              <button onClick={m.resetCustomSession} className={`text-xs px-1 ${isDarkMode ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-700"}`} title={t("session.cancel")}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          ) : (
            <button
              onClick={() => m.setShowSessionDropdown((s) => !s)}
              className={`${inpCls} w-full sm:w-64 text-left flex items-center justify-between gap-2 cursor-pointer hover:border-primary-500`}
              title={m.session}
            >
              <span className={`truncate ${isDarkMode ? "text-slate-100" : "text-gray-800"}`}>{m.session || t("session.selectPlaceholder")}</span>
              <FontAwesomeIcon icon={faChevronDown} className="text-[10px] shrink-0 text-gray-400" />
            </button>
          )}

          {m.showSessionDropdown && !m.customSession && (
            <div className={`absolute top-full left-0 mt-1 w-[calc(100vw-1.5rem)] max-w-[24rem] sm:w-96 rounded shadow-xl z-50 overflow-hidden ${
              isDarkMode ? "bg-dark-surface border border-dark-border" : "bg-white border border-gray-200"
            }`}>
              <input
                autoFocus
                placeholder={t("session.searchPlaceholder")}
                value={m.sessionSearch}
                onChange={(e) => m.setSessionSearch(e.target.value)}
                className={`w-full text-sm px-3 py-2 outline-none border-b ${
                  isDarkMode ? "bg-dark-bg border-dark-border text-slate-100 placeholder:text-gray-500 focus:border-primary-500" : "bg-gray-50 border-gray-200 text-gray-800 placeholder:text-gray-400 focus:border-primary-500"
                }`}
              />
              <div className="max-h-80 overflow-auto">
                <div
                  onClick={m.startCustomSession}
                  className={`px-3 py-2 text-xs cursor-pointer border-b ${
                    isDarkMode ? "text-amber-400 hover:bg-dark-card border-dark-border" : "text-amber-600 hover:bg-gray-50 border-gray-100"
                  }`}
                >
                  <FontAwesomeIcon icon={faKeyboard} className="mr-1.5" />
                  {t("session.writeManual")}
                </div>
                {m.currentServerType && (
                  <div className={`px-3 py-1 text-[10px] border-b ${
                    isDarkMode ? "text-primary-500 bg-dark-bg border-dark-border" : "text-primary-700 bg-gray-50 border-gray-100"
                  }`}>
                    {t("session.showingFor", { server: m.currentServerType })}
                  </div>
                )}
                {m.filteredPresets.map((group) => {
                  const filtered = group.sessions.filter(
                    (s) => !m.sessionSearch || s.toLowerCase().includes(m.sessionSearch.toLowerCase())
                  );
                  if (filtered.length === 0) return null;
                  return (
                    <div key={group.server}>
                      <div className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider sticky top-0 ${
                        isDarkMode ? "text-gray-400 bg-dark-bg" : "text-gray-500 bg-gray-50"
                      }`}>
                        {group.server} ({filtered.length})
                      </div>
                      {filtered.map((s) => (
                        <div
                          key={s}
                          onClick={() => m.selectSession(s)}
                          className={`px-3 py-1.5 text-xs cursor-pointer ${
                            m.session === s
                              ? "bg-primary-500 text-dark-bg font-semibold"
                              : isDarkMode ? "text-slate-200 hover:bg-dark-card" : "text-gray-700 hover:bg-gray-100"
                          }`}
                        >
                          {s}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {m.showSessionDropdown && (
            <div className="fixed inset-0 z-40" onClick={m.closeDropdown} />
          )}
        </div>

        {/* Stream + Restart group */}
        <div className="w-full sm:w-auto flex items-center gap-2">
          {!m.streaming
            ? <button onClick={m.startStream} disabled={m.sshStatus !== "connected"} className={`${btnPrimary} flex-1 sm:flex-none`}>
                <FontAwesomeIcon icon={faPlay} className="mr-1.5 text-xs" /> {t("stream.live")}
              </button>
            : <button onClick={m.stopStream} className={`${btnGhost} flex-1 sm:flex-none`}>
                <FontAwesomeIcon icon={faPause} className="mr-1 text-xs" /> {t("stream.pause")}
              </button>
          }

          {/* Color refleja si el WS está vivo (gris, no necesario) o muerto (verde, acción urgente). */}
          <button
            onClick={m.restartSession}
            disabled={m.sshStatus !== "connected" || !m.session}
            className={`${
              m.isWsDead
                ? "bg-emerald-500 hover:bg-emerald-400 border border-emerald-400 text-dark-bg animate-pulse"
                : isDarkMode
                  ? "bg-dark-card hover:bg-dark-surface border border-dark-border text-gray-400"
                  : "bg-gray-200 hover:bg-gray-300 border border-gray-300 text-gray-500"
            } rounded text-sm px-3 py-1.5 font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-1 sm:flex-none`}
            title={m.isWsDead ? t("restart.titleDead") : t("restart.titleActive")}
          >
            <FontAwesomeIcon icon={faRotateRight} className="mr-1.5 text-xs" />
            {t("restart.label")}
          </button>
        </div>

        <div className="hidden lg:block flex-1" />

        {/* Search + Auto-scroll group */}
        <div className="w-full sm:w-auto flex items-center gap-2 min-w-0">
          <div className="relative flex items-center flex-1 sm:flex-none min-w-0">
          <FontAwesomeIcon icon={faMagnifyingGlass} className="absolute left-2.5 text-xs pointer-events-none text-gray-400" />
          <input
            placeholder={t("search.terminalPlaceholder")}
            value={m.terminalSearch}
            onChange={(e) => m.setTerminalSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                m.jumpToNextMatch();
              }
            }}
            className={`${inpCls} w-full sm:w-48 pl-8`}
            title={t("search.enterHint")}
          />
          {m.terminalSearch && (
            <span className="absolute right-2.5 text-[10px] text-amber-400 font-medium pointer-events-none">
              {m.activeMatchIdx >= 0 ? `${m.activeMatchIdx + 1}/${terminalMatches}` : terminalMatches}
            </span>
          )}
        </div>

          <label className={`flex items-center gap-1.5 text-xs cursor-pointer select-none px-2.5 py-1.5 rounded transition-colors shrink-0 ${
            isDarkMode ? "text-gray-300 hover:bg-dark-card/60" : "text-gray-600 hover:bg-gray-100"
          }`}>
            <input type="checkbox" checked={m.autoScroll} onChange={(e) => m.setAutoScroll(e.target.checked)} className="accent-primary-500" />
            {t("search.autoScroll")}
          </label>
        </div>
      </div>

      {/* ─── MOBILE TABS (only < lg) ─────────────────────────────────────── */}
      <div className={`flex lg:hidden shrink-0 border-b ${
        isDarkMode ? "bg-dark-bg border-dark-border" : "bg-white border-gray-200"
      }`}>
        <button
          onClick={() => setMobileView("terminal")}
          className={`flex-1 px-3 py-2 text-xs font-semibold uppercase tracking-wide transition-colors cursor-pointer flex items-center justify-center gap-1.5 ${
            mobileView === "terminal"
              ? "text-primary-500 border-b-2 border-primary-500"
              : isDarkMode ? "text-gray-400 border-b-2 border-transparent" : "text-gray-500 border-b-2 border-transparent"
          }`}
        >
          {t("tabs.terminal")}
          {m.streaming && !m.isWsDead && <span className="text-primary-500 text-[10px] animate-pulse">●</span>}
        </button>
        <button
          onClick={() => setMobileView("xml")}
          className={`flex-1 px-3 py-2 text-xs font-semibold uppercase tracking-wide transition-colors cursor-pointer flex items-center justify-center gap-1.5 ${
            mobileView === "xml"
              ? "text-primary-500 border-b-2 border-primary-500"
              : isDarkMode ? "text-gray-400 border-b-2 border-transparent" : "text-gray-500 border-b-2 border-transparent"
          }`}
        >
          {t("tabs.xml")}
          {m.captures.length > 0 && (
            <span className={`text-[10px] px-1.5 rounded-full ${
              isDarkMode ? "bg-dark-card text-gray-300" : "bg-gray-200 text-gray-600"
            }`}>{m.captures.length}</span>
          )}
        </button>
      </div>

      {/* ─── BODY ────────────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col lg:grid lg:grid-cols-[1fr_380px] overflow-hidden">

        {/* TERMINAL (always dark for readability) */}
        <div className={`${mobileView === "terminal" ? "flex" : "hidden"} lg:flex flex-col lg:border-r border-dark-border overflow-hidden bg-dark-bg`}>
          <div className="px-4 py-1.5 border-b border-dark-border text-[11px] text-gray-300 flex items-center gap-2 shrink-0 bg-dark-bg">
            <span className="font-semibold text-white">{t("terminal.title")}</span>
            <span className="text-dark-border">|</span>
            <span className="text-gray-400 truncate" title={`${m.session}:${m.pane}`}>{m.session}:{m.pane}</span>
            {m.streaming && !m.isWsDead && <span className="text-primary-500 text-[10px] animate-pulse shrink-0">{t("stream.liveBadge")}</span>}
            {m.isWsDead && (
              <span className="text-red-400 text-[10px] font-bold shrink-0 flex items-center gap-1 bg-red-500/10 border border-red-500/30 px-1.5 py-0.5 rounded">
                {t("stream.wsPausedBadge")}
              </span>
            )}
            <div className="flex-1" />
            {m.terminalSearch && (
              <span className="text-[10px] text-amber-400 shrink-0">{t("search.matches", { count: terminalMatches })}</span>
            )}
          </div>

          <div ref={m.terminalContainerRef} className="flex-1 overflow-auto p-3 text-sm leading-relaxed whitespace-pre-wrap break-words bg-dark-bg text-slate-100 font-mono">
            {m.lines.length === 0
              ? <span className="text-gray-500">
                  {m.sshStatus === "connected" ? t("terminal.pressLive") : t("terminal.connectSsh")}
                </span>
              : m.lines.map((line, i) => {
                  const activeLineIdx = m.activeMatchIdx >= 0 ? m.matchPositions[m.activeMatchIdx] : -1;
                  const isActive = i === activeLineIdx;
                  if (m.terminalSearch && line.toLowerCase().includes(m.terminalSearch.toLowerCase())) {
                    const parts = line.split(new RegExp(`(${escapeRegex(m.terminalSearch)})`, "gi"));
                    return (
                      <div
                        key={i}
                        data-line-idx={i}
                        className={isActive ? "bg-amber-400/30 ring-1 ring-amber-400 rounded-sm" : "bg-primary-500/10"}
                      >
                        {parts.map((part, j) =>
                          part.toLowerCase() === m.terminalSearch.toLowerCase()
                            ? <mark key={j} className={isActive ? "bg-amber-400 text-dark-bg px-0.5 rounded font-semibold" : "bg-primary-500 text-dark-bg px-0.5 rounded font-semibold"}>{part}</mark>
                            : part
                        )}
                      </div>
                    );
                  }
                  return <div key={i} data-line-idx={i}>{line}</div>;
                })
            }
            <div ref={m.bottomRef} />
          </div>
        </div>

        {/* XML PANEL */}
        <div className={`${mobileView === "xml" ? "flex" : "hidden"} lg:flex flex-col overflow-hidden min-w-0 ${isDarkMode ? "bg-dark-surface" : "bg-gray-50"}`}>

          <div className={`px-4 py-1.5 border-b text-[11px] flex items-center gap-2 shrink-0 font-semibold ${
            isDarkMode ? "bg-dark-bg border-dark-border text-gray-300" : "bg-white border-gray-200 text-gray-600"
          }`}>
            <FontAwesomeIcon icon={faFileCode} className={isDarkMode ? "text-white" : "text-gray-800"} />
            <span className={isDarkMode ? "text-white" : "text-gray-800"}>{t("xml.title")}</span>
            {m.xmlStatus === "searching" && <span className="text-amber-400 text-[10px]">{t("xml.statusSearching")}</span>}
            {m.xmlStatus === "found" && <span className="text-primary-500 text-[10px]">{t("xml.statusFound")}</span>}
            {m.xmlStatus === "not_found" && <span className="text-red-400 text-[10px]">{t("xml.statusNotFound")}</span>}
            {m.xmlStatus === "cancelled" && <span className="text-gray-400 text-[10px]">{t("xml.statusCancelled")}</span>}
          </div>

          {/* Search form */}
          <div className={`p-3 border-b shrink-0 ${isDarkMode ? "bg-dark-surface border-dark-border" : "bg-white border-gray-200"}`}>
            <div className={`text-[11px] font-semibold uppercase tracking-wide mb-1.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>{t("xml.imeiPlate")}</div>
            <div className="flex gap-1.5 mb-2">
              <input placeholder={t("xml.imei")} value={m.imei} onChange={(e) => m.setImei(e.target.value)} maxLength={20} className={`${inpCls} flex-1 min-w-0`} />
              <input placeholder={t("xml.plate")} value={m.plate} onChange={(e) => m.setPlate(e.target.value.toUpperCase())} className={`${inpCls} w-20`} />
            </div>

            {m.xmlStatus !== "searching" ? (
              <button onClick={m.captureXml} disabled={m.sshStatus !== "connected" || !m.imei.trim()} className={`${btnPrimary} w-full`}>
                {t("xml.capture")}
              </button>
            ) : (
              <div>
                <ProgressBar current={m.progress.n} max={m.progress.max} />
                <div className="flex justify-between items-center">
                  <span className={`text-[11px] ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>{m.xmlMsg}</span>
                  <button onClick={m.cancelSearch} className="bg-transparent border border-red-300 rounded text-xs px-2 py-0.5 text-red-500 cursor-pointer hover:bg-red-50">
                    {t("xml.cancel")}
                  </button>
                </div>
              </div>
            )}
            {m.xmlStatus !== "searching" && m.xmlMsg && (
              <div className={`mt-1.5 text-[11px] ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>{m.xmlMsg}</div>
            )}
          </div>

          {/* Captures list */}
          <div className={`px-3 py-2 border-b shrink-0 ${isDarkMode ? "border-dark-border" : "border-gray-200"}`}>
            <span className={`text-[11px] font-semibold uppercase tracking-wide ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
              {t("xml.captures", { count: m.captures.length })}
            </span>
          </div>

          <div className={`overflow-auto ${m.selected ? "max-h-40" : "flex-1"}`}>
            {m.captures.length === 0 ? (
              <div className={`p-3 text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>{t("xml.noCaptures")}</div>
            ) : (
              m.captures.map((c) => (
                <div
                  key={c.id}
                  onClick={() => { m.setSelected(c); m.setShowRaw(false); }}
                  className={`px-3 py-2 cursor-pointer border-b border-l-[3px] ${
                    m.selected?.id === c.id
                      ? isDarkMode
                        ? "bg-dark-card border-b-dark-border border-l-primary-500"
                        : "bg-primary-50 border-b-gray-100 border-l-primary-500"
                      : isDarkMode
                        ? "bg-dark-surface border-b-dark-border border-l-transparent hover:bg-dark-card"
                        : "bg-white border-b-gray-100 border-l-transparent hover:bg-gray-50"
                  }`}
                >
                  <div className="flex justify-between mb-0.5">
                    <span className={`text-[13px] font-semibold ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>{c.plate || "—"}</span>
                    <span className={`text-[11px] ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>{c.ts.toLocaleTimeString()}</span>
                  </div>
                  <div className={`text-[11px] font-mono ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>{c.imei}</div>
                  <div className={`text-[10px] ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>{t("xml.attempt", { n: c.attempt })}</div>
                </div>
              ))
            )}
          </div>

          {/* Detail */}
          {m.selected && (
            <div ref={m.xmlDetailRef} className={`border-t p-3 flex-1 overflow-auto flex flex-col min-w-0 ${
              isDarkMode ? "border-dark-border bg-dark-surface" : "border-gray-200 bg-white"
            }`}>
              <div className="flex justify-between items-center mb-2.5">
                <span className={`text-[13px] font-semibold ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>
                  {m.selected.plate} · <span className={`font-mono ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>···{m.selected.imei.slice(-6)}</span>
                </span>
                <div className="flex gap-1.5">
                  <button onClick={() => m.setShowRaw((r) => !r)} className={btnGhost}>
                    {m.showRaw ? t("xml.summary") : t("xml.raw")}
                  </button>
                  <button onClick={m.copyXml} className={`${btnGhost} ${m.copied ? "!text-primary-600" : ""}`}>
                    <FontAwesomeIcon icon={m.copied ? faCheck : faCopy} className="mr-1" />
                    {m.copied ? t("xml.copied") : t("xml.copy")}
                  </button>
                </div>
              </div>
              {m.showRaw ? (
                <div className={`flex-1 overflow-auto border rounded p-2.5 text-[11px] font-mono whitespace-pre-wrap leading-relaxed ${
                  isDarkMode ? "bg-dark-bg border-dark-border text-gray-300" : "bg-gray-50 border-gray-200 text-gray-600"
                }`}>
                  {m.selected.xml}
                </div>
              ) : (
                <XmlSummary xml={m.selected.xml} isDarkMode={isDarkMode} />
              )}
              <div className="mt-3 flex gap-2">
                <button
                  onClick={m.screenshotXml}
                  className={`flex-1 rounded text-sm px-3 py-2 font-medium cursor-pointer flex items-center justify-center gap-2 transition-colors ${
                    isDarkMode
                      ? "bg-dark-card hover:bg-dark-bg border border-dark-border text-white"
                      : "bg-gray-800 hover:bg-gray-900 border border-gray-700 text-white"
                  }`}
                >
                  <FontAwesomeIcon icon={faImage} className="text-xs" />
                  {t("xml.screenshot")}
                </button>
                <button
                  onClick={m.downloadXml}
                  className="flex-1 rounded text-sm px-3 py-2 font-semibold cursor-pointer flex items-center justify-center gap-2 transition-colors bg-primary-500 hover:bg-primary-400 border border-primary-400 text-dark-bg"
                >
                  <FontAwesomeIcon icon={faDownload} className="text-xs" />
                  {t("xml.download")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* ─── TOAST NOTIFICATIONS ──────────────────────────────────────── */}
      {m.toasts.length > 0 && (
        <div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 max-w-sm">
          {m.toasts.map((toast) => (
            <div
              key={toast.id}
              className={`flex items-center gap-2 px-3.5 py-2.5 rounded-lg shadow-lg text-sm font-medium animate-slideIn ${TOAST_STYLES[toast.type]}`}
            >
              <span className="flex-1">{toast.message}</span>
              <button onClick={() => m.removeToast(toast.id)} className="opacity-70 hover:opacity-100 text-inherit shrink-0">
                <FontAwesomeIcon icon={faTimes} className="text-xs" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
